import { Form } from '@/pages/workgroup/form/StyledComponent';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from '@tigergraph/app-ui-lib/modal';
import { useForm, Controller } from 'react-hook-form';
import { FormTitleContainer } from '@/components/styled';
import TooltipLabel from '@/components/TooltipLabel';
import { Input } from '@tigergraph/app-ui-lib/input';
import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { FormControl } from '@/components/FormControl';
import { WorkSpaceType } from '@/pages/workgroup/type';

type Props = {
  workspaceTypes: WorkSpaceType[];
  sizeData?: SizeData;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (size: SizeData) => void;
  onContactUs: () => void;
};

export type SizeData = {
  size: number;
};

// https://graphsql.atlassian.net/wiki/spaces/TGDesignDocs/pages/3142713703/Workspace+Sizing#Configuration-Auto-Suggestion
// RawDataSizeInGBs * (1 - CompressionRatio) * 2 + 5
// Compression ratio is fixed; customers no longer enter it.
const COMPRESSION_RATIO = 0.5;

export function calculateSize(rawSize: number, workspaceTypes: WorkSpaceType[]): WorkSpaceType | undefined {
  const ramSizeInGBs = rawSize * (1 - COMPRESSION_RATIO) * 2 + 5;
  const availableTypes = workspaceTypes.filter((workspaceType) => !workspaceType.disabled);
  const types = availableTypes.length > 0 ? availableTypes : workspaceTypes;

  if (isNaN(ramSizeInGBs) || types.length === 0) {
    return types[0];
  }

  return types.find((workspaceType) => parseInt(workspaceType.memory) >= ramSizeInGBs);
}

export default function SizeSuggestion({ workspaceTypes, isOpen, sizeData, onCancel, onConfirm, onContactUs }: Props) {
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<SizeData>({
    mode: 'onBlur',
    defaultValues: {
      size: sizeData?.size || 10,
    },
  });

  const [css, theme] = useStyletron();

  const size = watch('size');
  const workspaceType = calculateSize(size, workspaceTypes);
  const exceedsCapacity = !workspaceType;

  return (
    <Modal onClose={onCancel} isOpen={isOpen}>
      <ModalHeader>Size Suggestion</ModalHeader>
      <Form>
        <ModalBody>
          <div>
            <div>
              <FormTitleContainer>
                <TooltipLabel
                  label="What is your raw data size?"
                  tooltip={`Could you please provide information on the size of your raw data? This will help us understand the scale of data we will be working with.`}
                />
              </FormTitleContainer>
              <FormControl error={errors?.size?.message}>
                <Controller
                  control={control}
                  name="size"
                  rules={{
                    validate: (value) => {
                      if (!value || value < 0) {
                        return 'data size must great than 0';
                      }
                      return true;
                    },
                  }}
                  render={({ field: { value, onChange, ref, ...field } }) => {
                    return (
                      <Input
                        placeholder="Enter the size of your raw data in gigabytes (GB)."
                        {...field}
                        value={value > 0 ? `${value}` : ''}
                        onChange={(e) => onChange(Number(e.currentTarget.value))}
                        error={!!errors?.size}
                        autoComplete="off"
                        endEnhancer="GB"
                        type="number"
                      />
                    );
                  }}
                />
              </FormControl>
            </div>
            <div>
              <FormTitleContainer>
                <TooltipLabel
                  label="Suggested Workspace Size"
                  tooltip="Recommended Workspace Size for Optimal Performance"
                />
              </FormTitleContainer>
              <div>
                {exceedsCapacity ? (
                  <p
                    className={css({
                      marginTop: '8px',
                      marginBottom: 0,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: theme.colors['text.secondary'],
                    })}
                  >
                    This data size exceeds the largest available workspace. Please{' '}
                    <button
                      type="button"
                      onClick={onContactUs}
                      className={css({
                        color: theme.colors.secondary800,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        font: 'inherit',
                        textDecoration: 'underline',
                      })}
                    >
                      contact us
                    </button>{' '}
                    for a custom solution.
                  </p>
                ) : (
                  <div
                    className={css({
                      display: 'grid',
                      gap: '32px',
                      marginTop: '8px',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      border: `1px solid ${theme.colors.divider}`,
                    })}
                  >
                    <StyleItem
                      $style={{
                        borderRight: `1px solid ${theme.colors.divider}`,
                      }}
                    >
                      <span>Size:</span>
                      <StyleSizeValue>{workspaceType.typeName}</StyleSizeValue>
                    </StyleItem>

                    <StyleItem
                      $style={{
                        borderRight: `1px solid ${theme.colors.divider}`,
                      }}
                    >
                      <span>CPU:</span>
                      <StyleSizeValue>{workspaceType.cpu}</StyleSizeValue>
                    </StyleItem>
                    <StyleItem>
                      <span>MEMORY:</span>
                      <StyleSizeValue>{workspaceType.memory}</StyleSizeValue>
                    </StyleItem>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="secondary" type="button" onClick={onCancel}>
            Cancel
          </ModalButton>
          <ModalButton
            type="button"
            disabled={exceedsCapacity}
            onClick={handleSubmit((data, e) => {
              e?.stopPropagation();
              onConfirm({
                size: data.size,
              });
            })}
          >
            Apply
          </ModalButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

const StyleSizeValue = styled('span', ({ $theme }) => ({
  color: $theme.colors['text.primary'],
  marginLeft: '4px',
}));

const StyleItem = styled('div', ({ $theme }) => ({
  ...$theme.typography.Body2,
  fontSize: '14px',
  color: $theme.colors['text.secondary'],
  padding: '8px',
}));
